import { alpha, Box, Stack, Typography } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import LightTooltip from 'components/common/tooltip/LightTooltip';
import useBundleTranslation from 'i18n';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { renderHtml, renderMarkdown } from 'tools/safeHtml';
import { ProgressBlock, ProgressDetailValue, ProgressHistoryItem } from 'app/concierge/types';

// A running step silent this long gains a muted suffix on its line.
export const STILL_WORKING_MS = 8000;
export const TAKING_LONGER_MS = 30000;
// The running steps plus this many finished ones stay visible; older ones fold. The turn's
// first block may override the budget with `visible_steps`.
export const VISIBLE_DONE_STEPS = 6;
// The live counter on the running step advances once a second; under prefers-reduced-motion it
// advances every five seconds instead.
const TICK_MS = 1000;
const REDUCED_MOTION_TICK_MS = 5000;
// The details of a step open in the application's tooltip from a small "i" icon (Marius, 2026-09-08).
const DETAILS_TOOLTIP_DELAY_MS = 100;
// On a turn that used ONE model the model label is a grey suffix ("· Sales Data") and belongs
// only to the rows that query data (Marius, 2026-09-08); every other attempt-level row reads
// without it. On a turn that used TWO OR MORE models that rule does not apply: D256 (Marius,
// 2026-09-11) puts the model's name in bold at the FRONT of every labelled row, whatever its
// stage, so a reader can tell whose line each one is. Rows with no model of their own (the
// routing's verdict, the composition's own lines, the parent turn's lines) stay unmarked either way.
const DATA_STAGES = new Set(['write_query', 'get_data']);

interface FeedStep {
    id: string;
    stage: string;
    text: string;
    state: ProgressBlock['state'];
    startMs: number;
    endMs: number;
    ordinal: number;
    detail?: ProgressBlock['detail'];
    history: ProgressHistoryItem[];
    // A block whose contract version is not 1 renders as its text only.
    plain: boolean;
    // The model an attempt-level line belongs to, shown as a suffix; the parent's lines have none.
    modelLabel?: string;
    // The line's fill-ins; a model name among them is emphasised in the rendered text.
    params?: ProgressBlock['params'];
    // The line's translation key, kept so the history can show the completed form later.
    key?: string;
    // Routing attempts this step announced ("Trying {model}, attempt N"): the models seen and
    // the highest attempt number. Attempt 1 is the umbrella of the whole first try and is not a
    // row of the feed; a retry (attempt 2 or a new model) is, worded as the retry it is.
    attemptModels: string[];
    maxAttempt: number;
    retry?: { model: string; attempt: number };
    // Whether the reader has already watched this row: a step delivered in a started or updated
    // state was on screen. One rule governs the feed (Marius, 2026-09-22): a step the feed has
    // shown is never removed, whatever state it ends in.
    shown: boolean;
    // The row this one runs inside, from the step id; its depth under the top level; whether a
    // shown row runs inside it.
    parentId?: string;
    depth: number;
    container: boolean;
}

function wasOnScreen(state: ProgressBlock['state']): boolean {
    return state === 'started' || state === 'updated';
}

function noteAttempt(step: FeedStep, params: ProgressBlock['params']): void {
    // Each attempt and the widening now arrive with an identity of their own, and they say what
    // they are. The attempt bookkeeping, which hides the umbrella and words a retry, belongs to
    // the umbrella row alone; without this a lone attempt's own row would be hidden as one.
    if (step.stage === 'choose_model' && step.id.includes(':')) {
        return;
    }
    const attempt = params?.attempt;
    if (typeof attempt !== 'number') {
        step.retry = undefined;
        return;
    }
    const model = typeof params?.model === 'string' ? params.model : '';
    if (model !== '' && !step.attemptModels.includes(model)) {
        step.attemptModels.push(model);
    }
    step.maxAttempt = Math.max(step.maxAttempt, attempt);
    step.retry = attempt >= 2 || step.attemptModels.length > 1 ? { model, attempt } : undefined;
}

// Item 165: a model's display name reads as words, not an identifier (underscores to spaces),
// and stands out in bold wherever the feed names a model. The block keeps the raw name.
export function modelDisplayName(name: string): string {
    return name.replace(/_/g, ' ');
}

function ModelName({ name }: { name: string }) {
    return (
        <Box component="span" data-test="concierge_progress_model_name" sx={{ fontWeight: 600 }}>
            {modelDisplayName(name)}
        </Box>
    );
}

// The server renders "Using {{model}}" and "Trying {{model}}, attempt 1" as plain text; the
// raw model name in the line's params locates it so it can be emphasised.
function renderLineText(text: string, params: ProgressBlock['params']): React.ReactNode {
    const model = params?.model;
    if (typeof model !== 'string' || model === '' || !text.includes(model)) {
        return text;
    }
    const at = text.indexOf(model);
    return (
        <>
            {text.slice(0, at)}
            <ModelName name={model} />
            {text.slice(at + model.length)}
        </>
    );
}

// D256: how many distinct models the turn worked through. A block that names a model carries its
// uid too, so uids are the identity; a block that somehow arrives without one counts by its
// display name instead. Two or more means the feed traces every row back to its model.
export function distinctModelCount(blocks: ProgressBlock[]): number {
    const seen = new Set<string>();
    blocks.forEach((block) => {
        const identity = block.model_uid || block.model_display_name;
        if (identity) {
            seen.add(identity);
        }
    });

    return seen.size;
}

function buildSteps(blocks: ProgressBlock[]): { steps: FeedStep[]; terminal?: ProgressBlock; multiModel: boolean } {
    const byStep = new Map<string, FeedStep>();
    let terminal: ProgressBlock | undefined;

    blocks.forEach((block, index) => {
        const plain = block.v !== 1;
        if (!plain && block.stage === 'done') {
            terminal = block;
            // The streamed answer writes nothing between its first chunk and this block, so the
            // composing step and a planning round still open have no finish of their own: the
            // answer's arrival is their finish.
            byStep.forEach((step) => {
                if (step.state !== 'started' && step.state !== 'updated') {
                    return;
                }
                if (step.stage === 'compose' || step.stage === 'write_query') {
                    step.state = 'done';
                    step.endMs = block.elapsed_ms;
                }
            });
            return;
        }
        const id = plain ? `unversioned-${index}` : block.step;
        const step = byStep.get(id);
        if (!step) {
            byStep.set(id, {
                id,
                stage: plain ? '' : block.stage,
                text: block.text,
                state: block.state,
                startMs: block.elapsed_ms,
                endMs: block.elapsed_ms,
                ordinal: block.ordinal,
                detail: block.detail,
                history: [],
                plain,
                modelLabel: block.model_display_name || undefined,
                params: block.params,
                key: block.key,
                attemptModels: [],
                maxAttempt: 0,
                shown: wasOnScreen(block.state),
                depth: 0,
                container: false,
            });
            noteAttempt(byStep.get(id) as FeedStep, block.params);
            return;
        }
        const carried = block.detail?.history;
        if (Array.isArray(carried)) {
            step.history = carried;
        } else if (step.text !== block.text && !step.history.some((item) => item.text === step.text)) {
            // A repeat of the same identity re-runs its lines; each distinct line joins the history once.
            step.history.push({ text: step.text, elapsed_ms: step.endMs, key: step.key, params: step.params });
        }
        step.text = block.text;
        step.shown = step.shown || wasOnScreen(block.state);
        step.state = block.state;
        step.endMs = block.elapsed_ms;
        step.ordinal = block.ordinal;
        if (block.detail) {
            step.detail = block.detail;
        }
        if (block.model_display_name) {
            step.modelLabel = block.model_display_name;
        }
        step.params = block.params;
        step.key = block.key;
        noteAttempt(step, block.params);
    });

    // A step that ends skipped after the reader watched it stays, as a neutral completed row;
    // one whose only delivered blocks are skipped was never on screen and stays hidden.
    const steps = [...byStep.values()].filter((step) => step.state !== 'skipped' || step.shown);
    nestSteps(steps);

    return {
        steps,
        terminal,
        multiModel: distinctModelCount(blocks) >= 2,
    };
}

// A routed turn's rows run inside one another (white, 2026-09-25: seven rows summed to about twice
// the answer's time). The step id names the nesting: a relayed row (attempt<N>:<step>) runs inside
// its attempt's row (choose_model:attempt<N>), or inside the choice row when the attempt has no
// row of its own, and every choose_model:<slot> row runs inside the choice row (choose_model#<run>)
// opened before it. Each row's elapsed spans its children's, so a row with children reads as the
// turn-clock time it closed at, and only the rows without children show a span.
function nestSteps(steps: FeedStep[]): void {
    const byId = new Map(steps.map((step) => [step.id, step]));
    let choice: FeedStep | undefined = steps.find((step) => /^choose_model#\d+$/.test(step.id));
    steps.forEach((step) => {
        if (/^choose_model#\d+$/.test(step.id)) {
            choice = step;
            return;
        }
        const relayed = /^attempt(\d+):/.exec(step.id);
        if (relayed) {
            const attemptRow = `choose_model:attempt${relayed[1]}`;
            step.parentId = byId.has(attemptRow) ? attemptRow : choice?.id;
        } else if (step.id.startsWith('choose_model:')) {
            step.parentId = choice?.id;
        }
    });
    steps.forEach((step) => {
        let parent = step.parentId ? byId.get(step.parentId) : undefined;
        if (parent) {
            parent.container = true;
        }
        while (parent && step.depth < 2) {
            step.depth += 1;
            parent = parent.parentId ? byId.get(parent.parentId) : undefined;
        }
    });
}

// The page's not-applied filters (ProgressNarrator::NOT_APPLIED_DETAIL): one line per filter.
const LIST_DETAIL = 'not_applied';
const PAGE_NOT_APPLIED_KEY = 'progress.dates_filters.page_not_applied';

// Detail labels belong to the backend bundle (design 5.7); a field with no key there shows
// its own name rather than the untranslated key.
function detailEntries(detail: ProgressBlock['detail']): [string, ProgressDetailValue][] {
    return Object.entries(detail ?? {}).filter(
        ([label, value]) => label !== 'history' && value !== undefined && value !== '',
    ) as [string, ProgressDetailValue][];
}

export default function ProgressFeed({
    blocks,
    active = false,
    onCancel,
    filtersApplied = '',
}: {
    blocks: ProgressBlock[];
    // The turn is still in flight; without it an unfinished step reads as stopped.
    active?: boolean;
    onCancel?: () => void;
    // The answer's "Filters Applied" lines (markdown), cut out of the answer and shown as the feed's last row.
    filtersApplied?: string;
}) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const { steps, terminal, multiModel } = useMemo(() => buildSteps(blocks), [blocks]);
    const [foldOpen, setFoldOpen] = useState(false);
    const [stepsOpen, setStepsOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);

    // Tool calls of the same family can overlap, and a routing attempt's own steps arrive while
    // the parent's "Trying {model}" step stays open, so a turn can have several unfinished steps
    // at once. Exactly one of them is live: the row whose latest event is newest (the highest
    // ordinal). It alone pulses, ticks and carries Cancel; the others freeze where they lost the
    // live role and wake up again if a later event touches them. The terminal block ends the
    // turn, and nothing keeps ticking after it.
    const runningSteps = terminal
        ? []
        : steps
              .filter((step) => !step.plain && (step.state === 'started' || step.state === 'updated'))
              .sort((a, b) => a.ordinal - b.ordinal);
    const activeStep = runningSteps.length > 0 ? runningSteps[runningSteps.length - 1] : undefined;
    const runningKey = runningSteps.map((step) => `${step.id}:${step.ordinal}`).join('|');
    const reducedMotion = useMemo(() => window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false, []);

    // The live counters run on the browser clock from each block's arrival, not on the
    // backend's elapsed_ms: startedAt anchors the step, eventAt its most recent block, and
    // frozenAt is set while the step is unfinished but not the live one. A step that takes the
    // live role back resumes from its frozen value: startedAt moves forward by the frozen span.
    const [now, setNow] = useState(() => Date.now());
    const clocks = useRef(
        new Map<string, { startedAt: number; eventAt: number; ordinal: number; frozenAt?: number }>(),
    );

    useEffect(() => {
        const at = Date.now();
        runningSteps.forEach((step) => {
            const live = step === activeStep;
            const clock = clocks.current.get(step.id);
            if (!clock) {
                clocks.current.set(step.id, {
                    startedAt: at,
                    eventAt: at,
                    ordinal: step.ordinal,
                    frozenAt: live ? undefined : at,
                });
                return;
            }
            if (clock.ordinal !== step.ordinal) {
                clock.eventAt = at;
                clock.ordinal = step.ordinal;
            }
            if (live && clock.frozenAt !== undefined) {
                clock.startedAt += at - clock.frozenAt;
                clock.eventAt = at;
                clock.frozenAt = undefined;
            } else if (!live && clock.frozenAt === undefined) {
                clock.frozenAt = at;
            }
        });
        setNow(at);
    }, [runningKey]);

    // A turn that ended without a terminal block was stopped; its counters freeze there.
    const stopped = !terminal && !active && runningSteps.length > 0;
    const hasRunning = runningSteps.length > 0 && !stopped;
    // The clock only ticks while a step is running: a finished, stopped or frozen line shows a fixed
    // time, so no timer is kept once nothing is live.
    useEffect(() => {
        if (!hasRunning) {
            return undefined;
        }
        const timer = setInterval(() => setNow(Date.now()), reducedMotion ? REDUCED_MOTION_TICK_MS : TICK_MS);
        return () => clearInterval(timer);
    }, [hasRunning, reducedMotion]);

    // Enter and Space activate the div-with-role controls the way a real button would.
    const activateOnKey = (action: () => void) => (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            action();
        }
    };

    // Under a finished step the earlier lines read in the completed tense ("Searched for reports
    // relevant to the question"); a step still running keeps them as they were ("Searching ...").
    // The completed form is the line's key with a _done suffix where the bundle carries one.
    const historyText = (step: FeedStep, item: ProgressHistoryItem): string => {
        const finished = step.state === 'done' || step.state === 'failed';
        if (!finished || !item.key) {
            return item.text;
        }
        return t(`${item.key}_done`, { ...(item.params ?? {}), defaultValue: item.text });
    };

    // The tooltip carries what the expandable section used to: the narrative first (the step's
    // earlier lines; one reads plain, two or more under a "What happened" heading, numbered), then
    // the detail pairs below it (Marius, 2026-09-08: the text leads, the measures and dimensions
    // follow).
    const renderDetails = (step: FeedStep, detail: [string, ProgressDetailValue][]) => (
        <Box
            data-test={`concierge_progress_details_${step.id}`}
            sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 1.5, rowGap: 0.375 }}
        >
            {step.history.length > 0 && (
                <Box
                    data-test="concierge_progress_history"
                    sx={{ gridColumn: '1 / -1', mb: detail.length > 0 ? 0.5 : 0, pb: detail.length > 0 ? 0.75 : 0 }}
                >
                    {step.history.length === 1 ? (
                        // One item reads as a plain line: no heading, no numbering (Marius, 2026-09-08).
                        <Typography variant="body2">{historyText(step, step.history[0])}</Typography>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                {t('progress.feed.what_happened')}
                            </Typography>
                            <Box component="ol" sx={{ m: 0, pl: 2 }}>
                                {step.history.map((item) => (
                                    <Typography component="li" variant="body2" key={item.text}>
                                        {historyText(step, item)}
                                    </Typography>
                                ))}
                            </Box>
                        </>
                    )}
                </Box>
            )}
            {detail.map(([label, value]) =>
                label === LIST_DETAIL && Array.isArray(value) ? (
                    // the step's own line already names the list, so it spans the tooltip with no label
                    <Box component="ul" key={label} sx={{ gridColumn: '1 / -1', m: 0, pl: 2 }}>
                        {(value as string[]).map((line) => (
                            <Typography component="li" variant="body2" key={line}>
                                {line}
                            </Typography>
                        ))}
                    </Box>
                ) : (
                    <React.Fragment key={label}>
                        <Typography variant="body2" color="text.secondary">
                            {t(`progress.label.${label}`, { defaultValue: label.replace(/_/g, ' ') })}
                        </Typography>
                        <Typography variant="body2">
                            {Array.isArray(value) ? value.join(', ') : String(value)}
                        </Typography>
                    </React.Fragment>
                ),
            )}
        </Box>
    );

    const renderStep = (step: FeedStep) => {
        if (step.plain) {
            return (
                <Box
                    key={step.id}
                    sx={{ fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1), py: 0.75 }}
                >
                    {step.text}
                </Box>
            );
        }

        // A step still open at the terminal line was never finished; it must not read as done.
        const unfinished = !!terminal && (step.state === 'started' || step.state === 'updated');
        const isStopped = unfinished || (stopped && step === activeStep);
        const isRunning = !isStopped && step === activeStep;
        // Unfinished but not live: closed for now, with the elapsed it had when it lost the role.
        const isFrozen = !unfinished && step !== activeStep && runningSteps.includes(step);
        const detail = detailEntries(step.detail);
        const hasDetails = detail.length > 0 || step.history.length > 0;
        const clock = clocks.current.get(step.id);
        const silenceMs = now - (clock?.eventAt ?? now);
        const durationMs = step.endMs - step.startMs;
        const clockEnd = isFrozen ? (clock?.frozenAt ?? now) : now;
        const clockSeconds = Math.max(0, Math.floor((clockEnd - (clock?.startedAt ?? clockEnd)) / 1000));
        // A timing that would read as zero at the displayed precision is not shown at all (Marius,
        // 2026-09-08): live and frozen rows under one second, finished rows under 0.05 s.
        const clockText = clockSeconds > 0 ? `${clockSeconds} s` : '';
        const durationText = (durationMs / 1000).toFixed(1);
        const closedText = (step.endMs / 1000).toFixed(1);
        const finishedText = step.container
            ? step.endMs > 0 && closedText !== '0.0'
                ? t('progress.feed.elapsed_at', { seconds: closedText, defaultValue: 'elapsed at {{seconds}} s' })
                : ''
            : durationMs > 0 && durationText !== '0.0'
              ? `${durationText} s`
              : '';
        // A routing step waits on the model's own background request, whose silence says nothing
        // about this turn being unusually slow; it keeps the "still working" wording throughout.
        const waitingOnModel = step.stage === 'choose_model';
        // Multi-model composition (lane 2): a leg's step sits under the composing step, indented.
        const isLeg = typeof step.params?.leg === 'number' || /:leg\d+$/.test(step.id);
        const heartbeat =
            isRunning && silenceMs >= STILL_WORKING_MS
                ? t(
                      waitingOnModel
                          ? 'progress.feed.model_working'
                          : silenceMs >= TAKING_LONGER_MS
                            ? 'progress.feed.taking_longer'
                            : 'progress.feed.still_working',
                  )
                : '';

        return (
            <Box key={step.id}>
                <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1}
                    data-test={`concierge_progress_step_${step.id}`}
                    data-leg={isLeg ? String(step.params?.leg ?? '') : undefined}
                    data-depth={step.depth > 0 ? String(step.depth) : undefined}
                    sx={{
                        py: 0.75,
                        paddingLeft: Math.max(isLeg ? 3 : 0, step.depth * 3),
                        fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                        lineHeight: '18px',
                    }}
                >
                    <Box
                        data-test={`concierge_progress_mark_${
                            isRunning ? 'running' : isStopped ? 'stopped' : isFrozen ? 'frozen' : step.state
                        }`}
                        sx={{
                            width: '16px',
                            minHeight: '17px',
                            display: 'inline-flex',
                            flexShrink: 0,
                            pt: '1px',
                            fontSize: '16px',
                        }}
                    >
                        {isRunning ? (
                            <Box
                                sx={{
                                    backgroundColor: 'primary.main',
                                    borderRadius: '50%',
                                    width: '16px',
                                    height: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <IconMi icon="arrow-right" sx={{ color: 'background.default', fontSize: '10px' }} />
                            </Box>
                        ) : isStopped ? (
                            <IconMi icon="times" sx={{ color: 'text.secondary' }} />
                        ) : step.state === 'skipped' ? (
                            // Watched, then given up on: neither a success nor a failure.
                            <IconMi icon="minus" sx={{ color: 'text.secondary' }} />
                        ) : step.key === PAGE_NOT_APPLIED_KEY && step.state !== 'failed' ? (
                            <IconMi icon="alert" sx={{ color: 'warning.main' }} />
                        ) : (
                            <IconMi
                                icon={step.state === 'failed' ? 'alert' : 'check'}
                                sx={{ color: step.state === 'failed' ? 'error.main' : 'success.main' }}
                            />
                        )}
                    </Box>
                    <Box
                        sx={{ flex: '1 1 auto', minWidth: 0, fontWeight: isRunning ? 500 : 400 }}
                        aria-live={isRunning ? 'polite' : undefined}
                    >
                        {step.modelLabel && multiModel && (
                            // D256: whose line this is, read first and in bold.
                            <Box
                                component="span"
                                data-test="concierge_progress_model_prefix"
                                data-test-variant="prefix"
                                sx={{ fontWeight: 600 }}
                            >
                                <ModelName name={step.modelLabel} />
                                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                    {' · '}
                                </Box>
                            </Box>
                        )}
                        {isStopped && !unfinished
                            ? t('progress.feed.stopped')
                            : step.retry
                              ? renderLineText(
                                    t('progress.feed.retry_attempt', {
                                        model: step.retry.model,
                                        attempt: step.retry.attempt,
                                        defaultValue: 'Trying {{model}} instead, attempt {{attempt}}',
                                    }),
                                    { model: step.retry.model },
                                )
                              : renderLineText(step.text, step.params)}
                        {Number(step.params?.count ?? 1) > 1 && (
                            // The narrator counts repeats of one step identity (step-narration-design.md).
                            <Box
                                component="span"
                                data-test="concierge_progress_repeat"
                                sx={{ color: 'text.secondary', fontWeight: 400 }}
                            >
                                {` \u00d7${step.params?.count}`}
                            </Box>
                        )}
                        {step.modelLabel && !multiModel && DATA_STAGES.has(step.stage) && (
                            <Box
                                component="span"
                                data-test="concierge_progress_model_label"
                                data-test-variant="suffix"
                                sx={{ color: 'text.secondary', fontWeight: 400 }}
                            >
                                {' · '}
                                <ModelName name={step.modelLabel} />
                            </Box>
                        )}
                        {heartbeat && (
                            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                {` · ${heartbeat}`}
                            </Box>
                        )}
                    </Box>
                    {isRunning || isFrozen || (isStopped && !unfinished)
                        ? clockText && (
                              <Box aria-hidden sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                  {clockText}
                              </Box>
                          )
                        : finishedText && (
                              <Box sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{finishedText}</Box>
                          )}
                    {hasDetails && (
                        <LightTooltip
                            title={renderDetails(step, detail)}
                            enterDelay={DETAILS_TOOLTIP_DELAY_MS}
                            enterNextDelay={DETAILS_TOOLTIP_DELAY_MS}
                            placement="bottom-start"
                        >
                            <Box
                                component="span"
                                role="button"
                                tabIndex={0}
                                aria-label={t('progress.feed.details')}
                                data-test={`concierge_progress_info_${step.id}`}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    cursor: 'default',
                                    flexShrink: 0,
                                    pt: '1px',
                                }}
                            >
                                <IconMi icon="info-circle" sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </Box>
                        </LightTooltip>
                    )}

                    {isRunning && onCancel && (
                        <Box
                            component="span"
                            role="button"
                            tabIndex={0}
                            data-test="concierge_progress_cancel"
                            onClick={(event: React.MouseEvent) => {
                                event.stopPropagation();
                                onCancel();
                            }}
                            onKeyDown={(event: React.KeyboardEvent) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onCancel();
                                }
                            }}
                            sx={{ color: 'primary.main', cursor: 'pointer' }}
                        >
                            {t('progress.feed.cancel')}
                        </Box>
                    )}
                </Stack>
            </Box>
        );
    };

    const renderFiltersApplied = () =>
        filtersApplied.trim() !== '' && (
            <Box data-test="concierge_progress_filters_applied">
                <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1}
                    role="button"
                    tabIndex={0}
                    aria-expanded={filtersOpen}
                    data-test="concierge_progress_filters_applied_toggle"
                    onClick={() => setFiltersOpen(!filtersOpen)}
                    onKeyDown={activateOnKey(() => setFiltersOpen(!filtersOpen))}
                    sx={{
                        py: 0.75,
                        cursor: 'pointer',
                        fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                        lineHeight: '18px',
                        '&:hover': {
                            color: 'primary.main',
                        },
                    }}
                >
                    <Box sx={{ width: '16px', minHeight: '17px', display: 'inline-flex', flexShrink: 0, pt: '1px' }}>
                        <IconMi icon="check" sx={{ color: 'success.main', fontSize: '16px' }} />
                    </Box>
                    <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>{t('progress.feed.filters_applied')}</Box>
                    <IconMi
                        icon={filtersOpen ? 'chevron-up' : 'chevron-down'}
                        sx={{ fontSize: '16px', flexShrink: 0, pt: '1px' }}
                    />
                </Stack>
                {filtersOpen && (
                    <Box
                        data-test="concierge_progress_filters_applied_lines"
                        sx={{
                            pl: 3,
                            pb: 0.75,
                            fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                            lineHeight: '18px',
                            wordBreak: 'break-word',
                            '& p': { m: 0 },
                            '& ul, & ol': { m: 0, pl: 2 },
                            '& ul, & ul li': { listStyle: 'circle !important' },
                            '& ol, & ol li': { listStyle: 'decimal !important' },
                        }}
                    >
                        {renderHtml(renderMarkdown(filtersApplied))}
                    </Box>
                )}
            </Box>
        );

    if (terminal) {
        return (
            <Box role="status" data-test="concierge_progress_feed" sx={{ mb: 0.5 }}>
                <Stack direction="row" alignItems="flex-end" spacing={1}>
                    <Box
                        sx={{
                            color: 'text.secondary',
                            fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                            flexGrow: 1,
                            alignSelf: 'center',
                        }}
                    >
                        {terminal.text}
                    </Box>
                    <Box
                        role="button"
                        tabIndex={0}
                        data-test="concierge_progress_show_steps"
                        onClick={() => setStepsOpen(!stepsOpen)}
                        onKeyDown={activateOnKey(() => setStepsOpen(!stepsOpen))}
                        sx={{
                            flexShrink: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            pl: 2,
                            pr: 1,
                            borderRadius: stepsOpen ? '4px 4px 0 0 ' : '4px',
                            backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
                            fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                            '&:hover': {
                                color: 'primary.main',
                            },
                        }}
                    >
                        <Box>{t(stepsOpen ? 'progress.feed.hide_steps' : 'progress.feed.show_steps')}</Box>

                        <IconMi
                            icon={stepsOpen ? 'chevron-up' : 'chevron-down'}
                            sx={{ fontSize: '16px', my: '5px', ml: 1.75 }}
                        />
                    </Box>
                </Stack>
                {stepsOpen && (
                    <Box
                        sx={{
                            backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
                            borderRadius: '4px 0 4px 4px',
                            py: 1.5,
                            px: 2,
                        }}
                    >
                        {steps.map(renderStep)}
                        {renderFiltersApplied()}
                    </Box>
                )}
            </Box>
        );
    }

    const budget = blocks[0]?.visible_steps;
    const visibleSteps = typeof budget === 'number' && budget > 0 ? Math.floor(budget) : VISIBLE_DONE_STEPS;
    const finished = steps.filter((step) => !runningSteps.includes(step));
    const foldedCount = foldOpen ? 0 : Math.max(0, finished.length - visibleSteps);

    return (
        <Box
            role="status"
            data-test="concierge_progress_feed"
            sx={{
                mb: 0.5,
                backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
                borderRadius: '4px',
                py: 1.5,
                px: 2,
            }}
        >
            {foldedCount > 0 && (
                <Box
                    role="button"
                    tabIndex={0}
                    data-test="concierge_progress_earlier_steps"
                    onClick={() => setFoldOpen(true)}
                    onKeyDown={activateOnKey(() => setFoldOpen(true))}
                    sx={{
                        color: 'primary.main',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        py: 0.75,
                        fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                        '&:hover': {
                            opacity: 0.75,
                        },
                    }}
                >
                    <IconMi icon={'chevron-down'} sx={{ fontSize: '16px', mr: 1 }} />
                    {t('progress.feed.earlier_steps', {
                        count: foldedCount,
                        // The plural forms ship with the bundle so a host whose runtime i18n predates them
                        // still reads "1 earlier step".
                        defaultValue: foldedCount === 1 ? '{{count}} earlier step' : '{{count}} earlier steps',
                    })}
                </Box>
            )}
            {finished.slice(foldedCount).map(renderStep)}
            {runningSteps.map(renderStep)}
            {/* a turn that ended without its terminal line still shows the filters cut out of its answer */}
            {!active && renderFiltersApplied()}
        </Box>
    );
}
